import {
    BuildingOffice2Icon,
    ChatBubbleLeftRightIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import type { FilterOption } from '@odo/shared-ui';
import { SearchSelect } from '../../../billing/SearchSelect';
import type { PracticeDetail } from '../../../../services/practiceDetail';
import { MetaCard } from './MetaCard';

function doctorLine(practice: PracticeDetail): string | null {
    const first = practice.doctor?.drFirstName?.trim();
    const last = practice.doctor?.drLastName?.trim();
    const name = first || last ? `Dr. ${[first, last].filter(Boolean).join(' ')}` : null;
    const city = practice.address?.city;
    const state = practice.address?.state;
    const location = city && state ? `${city}, ${state}` : null;
    return [name, location].filter(Boolean).join(' · ') || null;
}

function InsightChip({ icon, label }: { icon: React.ReactNode; label: string }) {
    return (
        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded-full h-6 px-2 text-[11px] font-semibold whitespace-nowrap">
            <span className="text-gray-400">{icon}</span>
            {label}
        </span>
    );
}

interface PracticeSummaryProps {
    practice: PracticeDetail;
    totalCommunications: number;
    openCommunicationsCount: number;
}

// Identity + counts, not a field list - who this thread belongs to and how
// busy that relationship has been, at a glance.
function PracticeSummary({
    practice,
    totalCommunications,
    openCommunicationsCount
}: PracticeSummaryProps) {
    const doctorAndLocation = doctorLine(practice);
    return (
        <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 text-gray-500 flex items-center justify-center shrink-0">
                <BuildingOffice2Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
                <span className="block text-[14px] font-bold text-gray-900 truncate">
                    {practice.name}
                </span>
                {doctorAndLocation && (
                    <span className="block text-[12px] text-gray-500 truncate">
                        {doctorAndLocation}
                    </span>
                )}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <InsightChip
                        icon={<ChatBubbleLeftRightIcon className="h-3 w-3" />}
                        label={`${totalCommunications} communication${totalCommunications === 1 ? '' : 's'}`}
                    />
                    <InsightChip
                        icon={<ClockIcon className="h-3 w-3" />}
                        label={`${openCommunicationsCount} open thread${openCommunicationsCount === 1 ? '' : 's'}`}
                    />
                </div>
            </div>
        </div>
    );
}

interface PracticeMetaCardProps {
    /** True: shows a practice picker. False: shows a read-only practice summary. */
    editable: boolean;
    /** Known synchronously on pick, unlike `practice` (resolves after the detail fetch). */
    practiceId: string | null;
    /** Null until the practice detail fetch resolves. */
    practice: PracticeDetail | null;
    totalCommunications: number;
    openCommunicationsCount: number;
    practiceOptions: readonly FilterOption[];
    isPracticeOptionsLoading: boolean;
    onPracticeChange: (practiceId: string) => void;
}

export function PracticeMetaCard({
    editable,
    practiceId,
    practice,
    totalCommunications,
    openCommunicationsCount,
    practiceOptions,
    isPracticeOptionsLoading,
    onPracticeChange
}: PracticeMetaCardProps) {
    if (!editable) {
        return (
            <MetaCard title="Practice" pad={false}>
                <div className="px-4 py-3.5">
                    {practice ? (
                        <PracticeSummary
                            practice={practice}
                            totalCommunications={totalCommunications}
                            openCommunicationsCount={openCommunicationsCount}
                        />
                    ) : (
                        <p className="text-[12px] text-gray-400">Loading practice…</p>
                    )}
                </div>
            </MetaCard>
        );
    }

    return (
        // clip={false}: SearchSelect's dropdown has no open-state callback
        // to key a conditional clip off of, so this card never clips.
        <MetaCard title="Practice" clip={false}>
            <div className="flex flex-col gap-1.5">
                <SearchSelect
                    value={practiceId ?? ''}
                    options={practiceOptions}
                    onChange={onPracticeChange}
                    placeholder="Search practice or doctor"
                    searchPlaceholder="Search practices..."
                    disabled={isPracticeOptionsLoading}
                />
                {/* Copy adapted from the redesign's "lands in the review
                    queue" hint - we have no such queue; a practice-less
                    thread is findable under the "No practice yet" filter
                    instead. */}
                <span className="text-[11px] text-gray-400 leading-snug">
                    Can be saved without one. Find it later under &ldquo;No practice yet&rdquo;.
                </span>
            </div>
            {practiceId && practice && (
                <PracticeSummary
                    practice={practice}
                    totalCommunications={totalCommunications}
                    openCommunicationsCount={openCommunicationsCount}
                />
            )}
            {practiceId && !practice && (
                <p className="text-[12px] text-gray-400">Loading practice…</p>
            )}
        </MetaCard>
    );
}
